import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { InvoiceService } from "@services";
import utils from "@utils";
import { Loader } from "../../../components";

import "./PaymentReceipt.scss";

const PaymentReceipt = ({ router, user }) => {
	const { lang } = global;
	const { params, push } = router;
	const { TimeZoneId } = user;
	const [loading, setLoading] = useState(true);
	const [invoice, setInvoice] = useState({});

	const goToPayments = () => {
		push("/payments/list/payment");
	};

	useEffect(() => {
		InvoiceService.getReceiptDetails(params.id)
			.then(({ data }) => {
				let { TransactionSummaryItems } = data;
				TransactionSummaryItems = TransactionSummaryItems.map(item => {
					if (item.AdditionalInvoiceItems.length > 0) {
						return {
							...item,
							AdditionalItemTotal: item.AdditionalInvoiceItems.map(
								({ ItemSubtotal }) => parseFloat(ItemSubtotal),
							).reduce((t, c) => t + c),
						};
					}
					return { ...item, AdditionalItemTotal: 0 };
				});
				setInvoice({ ...data, TransactionSummaryItems });
				setLoading(false);
			})
			.catch(() => goToPayments());
	}, []);

	const downloadPDF = () => {
		setLoading(true);
		InvoiceService.downloadReceiptPDF(params.id)
			.then(({ data }) => {
				utils.downloadReportFromBlobResponse(data, `rollkall_receipt_${params.id}.pdf`);
			})
			.catch(() =>
				utils.notify({
					type: "error",
					message: "Download failed. Please try again.",
					timeOut: 1500,
				}),
			)
			.finally(() => setLoading(false));
	};

	return (
		<div id="payment-receipt">
			<div className="receipt-subnav container-fluid hidden-print">
				<div className="row">
					<div className="col-sm-12">
						<button
							type="button"
							id="go-back"
							className="btn btn-link"
							onClick={goToPayments}
						>
							<i className="glyphicon glyphicon-menu-left" />
							&nbsp;
							{lang.t("Payments")}
						</button>
					</div>
				</div>
			</div>
			<div className="container">
				<Loader active={loading} />
				<div className="row">
					<div className="col-xs-8 col-md-10">
						<h1>
							<i className="rkf rkf-receipt" />
							&nbsp;
							{lang.t("Receipt")}
						</h1>
					</div>
					<div className="col-xs-4 col-md-2 text-right">
						<button
							type="button"
							id="print-receipt"
							className="btn btn-link"
							title="Print a copy of this receipt"
							onClick={() => window.print()}
						>
							<i className="glyphicon glyphicon-print" />
						</button>
						<button
							type="button"
							id="save-pdf"
							className="btn btn-link"
							title="Download this receipt as a PDF document"
							onClick={downloadPDF}
						>
							<i className="rkf rkf-pdf" />
						</button>
					</div>
				</div>
				<div className="row">
					<div className="col-xs-6 col-sm-12">
						<div className="label label-success text-uppercase">
							{lang.t("Paid")}{" "}
							{utils.formatDate(
								new Date(`${invoice.PaymentDate}Z`),
								"en-US",
								TimeZoneId,
							)}
						</div>
					</div>
					<div className="col-xs-6 col-sm-12 text-right trans-id">
						{lang.t("Transaction")} #{invoice.TransactionId}
					</div>
				</div>
				<div className="row table-headers hidden-xs">
					<div className="col-sm-1">
						<strong>{lang.t("Invoice")}</strong>
					</div>
					<div className="col-sm-4">
						<strong>{lang.t("Job")}</strong>
					</div>
					<div className="col-sm-3">
						<strong>{lang.t("Officer")}</strong>
					</div>
					<div className="col-sm-1">
						<strong>{lang.t("Hours")}</strong>
					</div>
					<div className="col-sm-1 text-right">
						<strong>{lang.t("Rate/Hr")}</strong>
					</div>
					<div className="col-sm-2 text-right">
						<strong>{lang.t("Amount Due")}</strong>
					</div>
				</div>
				<div className="line-items">
					<For
						each="item"
						of={invoice.TransactionSummaryItems || []}
					>
						<div
							key={`line-item_${item.AssignmentId}`}
							className="row"
						>
							<div className="col-sm-1">
								<Link to={`/payments/${item.InvoiceId}`}>
									<span className="visible-xs-inline">{lang.t("Invoice")} #</span>
									{item.InvoiceId}
								</Link>
							</div>
							<div className="col-sm-4">
								{item.Location}
								<br />
								{utils.formatDate(
									new Date(`${item.StartDateTime}Z`),
									"en-US",
									TimeZoneId,
								)}
								<br />
								<small className="text-muted">
									{utils.formatTime(
										new Date(`${item.StartDateTime}Z`),
										true,
										TimeZoneId,
									)}
									&nbsp;-&nbsp;
									{utils.formatTime(
										new Date(`${item.EndDateTime}Z`),
										true,
										TimeZoneId,
									)}
								</small>
							</div>
							<div className="col-sm-3">
								<strong className="visible-xs-inline">
									{lang.t("Officer")}:&nbsp;
								</strong>
								{item.Officer}
							</div>
							<div className="col-xs-4 col-sm-1">
								{item.Hours}
								<span className="visible-xs-inline">
									{item.Hours === 1 ? " hr" : " hrs"}
								</span>
							</div>
							<div className="col-xs-4 col-sm-1 rate">
								{utils.currencyFormatter(item.Rate, false)}
								<span className="visible-xs-inline">/hr</span>
							</div>
							<div className="col-xs-4 col-sm-2 text-right">
								<strong className="visible-xs-inline">
									{lang.t("Total")}:&nbsp;
								</strong>
								{utils.currencyFormatter(item.Amount)}
							</div>
							<If condition={item.AdditionalInvoiceItems.length > 0}>
								<div className="col-sm-7 col-sm-offset-5">
									<strong>{lang.t("Additional Items")}</strong>
								</div>
							</If>
							<For
								each="additem"
								of={item.AdditionalInvoiceItems}
							>
								<div
									className="col-xs-12 additem"
									key={`${item.InvoiceId}_${additem.ItemName.replace(" ", "")}`}
								>
									<div className="col-xs-3 col-sm-offset-5">
										{additem.ItemName}
									</div>
									<div className="col-xs-3 col-sm-1">
										{additem.ItemHours}
										<span className="visible-xs-inline">
											{item.Hours === 1 ? " hr" : " hrs"}
										</span>
									</div>
									<div className="col-xs-3 col-sm-1 rate">
										{utils.currencyFormatter(
											parseFloat(additem.ItemRate),
											false,
										)}
										<span className="visible-xs-inline">/hr</span>
									</div>
									<div className="col-xs-3 col-sm-2 text-right">
										{utils.currencyFormatter(parseFloat(additem.ItemSubtotal))}
									</div>
								</div>
							</For>
						</div>
					</For>
					<For
						each="coord"
						of={invoice.Organizations || []}
						index="idx"
					>
						<div
							key={(coord.Name || `coord_${idx}`).toLowerCase().replace(" ", "")}
							className="row"
						>
							<div className="col-xs-0 col-sm-5" />
							<div className="col-xs-12 col-sm-3">
								<strong>{lang.t("Coordination")}</strong>
								<br />
								{coord.Name}
							</div>
							<div className="col-xs-4 col-sm-1">
								{coord.Hours}
								<span className="visible-xs-inline text-lowercase">
									{" "}
									{lang.t("Hours")}
								</span>
							</div>
							<div className="col-xs-4 col-sm-1 rate">
								{utils.currencyFormatter(coord.Rate, false)}
								<span className="visible-xs-inline">/hr</span>
							</div>
							<div className="col-xs-4 col-sm-2 text-right">
								<strong className="visible-xs-inline">
									{lang.t("Total")}:&nbsp;
								</strong>
								{utils.currencyFormatter(coord.Amount)}
							</div>
						</div>
					</For>
				</div>
				<div className="receipt-totals">
					<div className="row">
						<div className="col-xs-8 col-sm-10 text-right">
							<strong>{lang.t("Total Officer Pay")}</strong>
						</div>
						<div className="col-xs-4 col-sm-2 text-right">
							{utils.currencyFormatter(invoice.TotalOfficerPay || 0)}
						</div>
					</div>
					<div className="row">
						<div className="col-xs-8 col-sm-10 text-right">
							<strong>{lang.t("Jurisdiction Fees")}</strong>
						</div>
						<div className="col-xs-4 col-sm-2 text-right">
							{utils.currencyFormatter(invoice.TotalJurisdictionFee || 0)}
						</div>
					</div>
					<div className="row">
						<div className="col-xs-8 col-sm-10 text-right">
							<strong>{lang.t("Coordination Fees")}</strong>
						</div>
						<div className="col-xs-4 col-sm-2 text-right">
							{utils.currencyFormatter(invoice.TotalOrganizationFee || 0)}
						</div>
					</div>
					<div className="row">
						<div className="col-xs-8 col-sm-10 text-right">
							<strong>{lang.t("Service Fees")}</strong>
						</div>
						<div className="col-xs-4 col-sm-2 text-right">
							{utils.currencyFormatter(invoice.RKPayFee || 0)}
						</div>
					</div>
					<If
						condition={
							invoice.WorkerCompensationFee && invoice.WorkerCompensationFee > 0
						}
					>
						<div className="row">
							<div className="col-xs-8 col-sm-10 text-right">
								<strong>{lang.t("Workers' Compensation Fee")}</strong>
							</div>
							<div className="col-xs-4 col-sm-2 text-right">
								{utils.currencyFormatter(invoice.WorkerCompensationFee)}
							</div>
						</div>
					</If>
					<div className="row">
						<div className="col-xs-8 col-sm-10 text-right">
							<strong>{lang.t("Tax")}</strong>
						</div>
						<div className="col-xs-4 col-sm-2 text-right">
							{utils.currencyFormatter(invoice.Tax || 0)}
						</div>
					</div>
					<div className="row">
						<div className="col-xs-8 col-sm-10 text-right">
							<strong>{lang.t("Total")}</strong>
						</div>
						<div className="col-xs-4 col-sm-2 text-right">
							<strong>{utils.currencyFormatter(invoice.Total || 0)}</strong>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

PaymentReceipt.propTypes = {
	router: PropTypes.object,
	user: PropTypes.object,
};

PaymentReceipt.defaultProps = {
	router: {},
	user: {},
};

export default PaymentReceipt;
